import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * UpgradeBanner - Shows a dismissible banner when user is on Free tier
 * and approaching or at their usage limits.
 * 
 * Props:
 * - vehicleCount: current number of vehicles
 * - vehicleLimit: max vehicles for current tier
 * - tierName: display name of current tier (e.g., "Free")
 * - onDismiss: optional callback when banner is dismissed
 */
export const UpgradeBanner = ({ vehicleCount, vehicleLimit, tierName, onDismiss }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    const dismissedAt = sessionStorage.getItem('upgradeBannerDismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < 30 * 60 * 1000) {
        setDismissed(true);
      } else {
        sessionStorage.removeItem('upgradeBannerDismissed');
      }
    }
  }, []);
  
  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('upgradeBannerDismissed', Date.now().toString());
    onDismiss?.();
  };
  
  const handleUpgrade = () => {
    navigate('/pricing');
  };
  
  if (dismissed) return null;
  
  // Only show if approaching limit (80%+) or at limit
  const usagePercent = vehicleLimit > 0 ? (vehicleCount / vehicleLimit) * 100 : 0;
  if (usagePercent < 80) return null;
  
  const atLimit = vehicleCount >= vehicleLimit;
  
  return (
    <div className={`${atLimit ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'} text-white px-4 py-3 relative`}>
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">{atLimit ? '🚀' : '📈'}</span>
          <div>
            <span className="font-semibold">
              {atLimit
                ? `You've reached your ${tierName || 'Free'} vehicle limit (${vehicleCount}/${vehicleLimit}).`
                : `You're using ${vehicleCount} of ${vehicleLimit} vehicles on your ${tierName || 'Free'} plan.`
              }
            </span>
            <span className="ml-1 hidden sm:inline">
              {atLimit ? 'Upgrade to add more vehicles.' : 'Upgrade for more capacity and features.'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleUpgrade}
            size="sm"
            className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold"
          >
            View Plans
          </Button>
          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * UpgradePromptModal - Modal that appears when user tries to access a tier-restricted feature
 */
export const UpgradePromptModal = ({ isOpen, onClose, featureName, requiredTier }) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-md w-full sm:mx-4 p-6 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
            <span className="text-3xl">�</span>
          </div>
          
          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Upgrade to Unlock {featureName || 'This Feature'}
          </h2>
          
          {/* Description */}
          <p className="text-gray-600 mb-6">
            {featureName || 'This feature'} requires the {requiredTier || 'Starter'} plan or higher.
            Upgrade to unlock more vehicles, co-host management, and advanced tools.
          </p>
          
          {/* Features list */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-gray-700 mb-2">Upgrade to get:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> More vehicles and team members
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Co-host management tools
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Plaid bank sync and Finance Hub
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> E-signatures and agreements
              </li>
            </ul>
          </div>
          
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleUpgrade}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              View Plans
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * useSubscriptionPrompt - Hook to listen for subscription error events
 */
export const useSubscriptionPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);
  
  useEffect(() => {
    const handleSubscriptionError = (event) => {
      setErrorDetail(event.detail);
      setShowPrompt(true);
    };
    
    window.addEventListener('subscriptionError', handleSubscriptionError);
    
    return () => {
      window.removeEventListener('subscriptionError', handleSubscriptionError);
    };
  }, []);
  
  const closePrompt = () => setShowPrompt(false);
  
  return { showPrompt, errorDetail, closePrompt };
};

// Backward-compatible exports
export const GracePeriodBanner = UpgradeBanner;
export const SubscribePromptModal = UpgradePromptModal;
export const useGracePeriodPrompt = useSubscriptionPrompt;

export default UpgradeBanner;
